import { useEffect, useState } from "react";
import { api, invalidateSiteCache } from "@/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";

const emptyForm = { titre: "", active: false, resume: "", description: "", pointsText: "", ordre: 0 };
const inputCls = "mt-2 w-full border-b border-bark/40 bg-transparent py-2.5 outline-none transition-[border-color] duration-300 focus:border-clay";

const AdminServices = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => api.get("/services").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      titre: form.titre,
      active: form.active,
      resume: form.resume,
      description: form.description,
      ordre: Number(form.ordre) || 0,
      points: form.pointsText.split("\n").map((p) => p.trim()).filter(Boolean),
    };
    try {
      if (form.id) {
        await api.put(`/admin/services/${form.id}`, payload);
        toast.success("Service mis à jour");
      } else {
        await api.post("/admin/services", payload);
        toast.success("Service ajouté");
      }
      invalidateSiteCache();
      setForm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enregistrement impossible");
    }
  };

  const toggle = async (s) => {
    await api.put(`/admin/services/${s.id}`, { ...s, active: !s.active, points: s.points || [] });
    toast.success(!s.active ? `« ${s.titre} » activé sur le site` : `« ${s.titre} » désactivé`);
    invalidateSiteCache();
    load();
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer « ${s.titre} » ?`)) return;
    await api.delete(`/admin/services/${s.id}`);
    toast.success("Service supprimé");
    invalidateSiteCache();
    load();
  };

  if (form) {
    return (
      <form onSubmit={save} className="max-w-2xl space-y-7" data-testid="admin-service-form">
        <h2 className="font-serif text-2xl tracking-tight">{form.id ? "Modifier" : "Nouveau"} service</h2>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] text-taupe">Titre</label>
          <input required value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} className={inputCls} data-testid="service-input-titre" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] text-taupe">Résumé (cartes, accueil)</label>
          <textarea rows={2} value={form.resume} onChange={(e) => setForm({ ...form, resume: e.target.value })} className={`${inputCls} resize-none`} data-testid="service-input-resume" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] text-taupe">Description détaillée (page Services)</label>
          <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} data-testid="service-input-description" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] text-taupe">Points forts (un par ligne)</label>
          <textarea rows={4} value={form.pointsText} onChange={(e) => setForm({ ...form, pointsText: e.target.value })} className={`${inputCls} resize-none`} data-testid="service-input-points" />
        </div>
        <label className="flex items-center gap-3 text-sm" data-testid="service-input-active">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-clay" />
          Visible sur le site (sinon affiché « Bientôt »)
        </label>
        <div className="flex gap-4">
          <button type="submit" data-testid="service-save" className="rounded-full bg-clay px-8 py-3.5 text-sm font-medium text-cream hover:bg-ink">Enregistrer</button>
          <button type="button" onClick={() => setForm(null)} data-testid="service-cancel" className="rounded-full border border-ink/20 px-8 py-3.5 text-sm text-bark hover:border-clay hover:text-clay">Annuler</button>
        </div>
      </form>
    );
  }

  return (
    <div data-testid="admin-services-list">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl tracking-tight">Services ({items.length})</h2>
        <button onClick={() => setForm({ ...emptyForm, ordre: items.length })} data-testid="service-add" className="flex items-center gap-2 rounded-full bg-clay px-5 py-2.5 text-sm font-medium text-cream hover:bg-ink">
          <Plus size={15} /> Ajouter
        </button>
      </div>
      <div className="mt-8 space-y-4">
        {items.map((s) => (
          <div key={s.id} className="flex items-center gap-5 bg-white/60 p-4" data-testid={`admin-service-${s.id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{s.titre}</p>
              <p className="text-sm text-taupe">{s.active ? "Visible sur le site" : "Affiché « Bientôt »"}</p>
            </div>
            <button onClick={() => toggle(s)} data-testid={`service-toggle-${s.id}`} className={`p-2 ${s.active ? "text-clay" : "text-taupe"} hover:text-ink`} aria-label={`Activer ou masquer ${s.titre}`}>
              {s.active ? <Eye size={17} /> : <EyeOff size={17} />}
            </button>
            <button onClick={() => setForm({ ...s, pointsText: (s.points || []).join("\n") })} data-testid={`service-edit-${s.id}`} className="p-2 text-bark hover:text-clay" aria-label={`Modifier ${s.titre}`}>
              <Pencil size={17} />
            </button>
            <button onClick={() => remove(s)} data-testid={`service-delete-${s.id}`} className="p-2 text-bark hover:text-clay" aria-label={`Supprimer ${s.titre}`}>
              <Trash2 size={17} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminServices;
